import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Activity,
  BadgeCheck,
  CalendarClock,
  Columns2,
  Columns3,
  Radar,
  CheckCircle2,
  Hourglass,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Rows2,
  Rows3,
  LogOut,
  Send,
  Crosshair,
  Sun,
  Trophy,
  UserPlus,
  Volume2,
  VolumeX,
  X,
  Globe,
} from 'lucide-react'
import type { CustomerStatus, LiveState } from '@acs/shared'
import { Tooltip } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { ConnectionDot, useConnectionMeta } from '@/components/shell/topbar'
import { RegionMark, RegionPlate } from '@/components/shell/region-plate'
import { RailSystemChip } from '@/components/system/rail-system-chip'
import { useLogout } from '@/hooks/use-auth'
import type { ConnectionMeta } from '@/components/shell/topbar'
import type { RealtimeStatus } from '@/hooks/use-realtime'
import { useNavCounts, useStats } from '@/hooks/use-stats'
import { useUiStore } from '@/stores/ui-store'
import {
  getEnabled as sfxEnabled,
  getReady as sfxReady,
  setEnabled as setSfxEnabled,
  subscribe as sfxSubscribe,
} from '@/lib/sfx'
import { cn } from '@/lib/cn'

/**
 * Left navigation.
 *
 * Six destinations, three headings, one selected state. The previous rail also
 * listed Agents, Office Accounts and Settings behind "SOON" pills — five links
 * that went nowhere, in a component whose entire job is to tell you where you
 * can go. An unbuilt screen is not a navigation item, so they are gone; when
 * those screens exist they can be added back in a line each.
 *
 * Selection is indigo and *only* indigo: a soft tint, accent text, and a 2px
 * bar on the left edge. Green is reserved for "this is running" — when the two
 * shared a hue you could not tell a selected list from a live one at a glance.
 *
 * Counts come from the same `['stats']` cache entry the SSE stream writes into,
 * so the numbers here move without a refetch.
 */

/** The live filter that the "Sessions" entry applies to the approved list. */
export const SESSION_LIVE_FILTER: LiveState[] = ['live', 'starting', 'stale']

/** External Mint console — lives outside this app, so it opens in a new tab. */
const MINT_BROWSER_URL = 'https://mintserver-production-5ff5.up.railway.app/'

type IconType = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>

/*
 * Deliberately search-free links, including Confirmed.
 *
 * Confirmed opens ordered by when the shift was claimed rather than by signup
 * date, and that could have been `search={{ sort: 'won_at' }}` here. It is not,
 * because then only THIS door would produce it — the command palette, a
 * bookmark and the redirect after confirming a selection would all still open
 * the same list in a different order. The default belongs to the view, and
 * lives in `defaultSortFor` next to the parse that reads it.
 */
const STATUS_ITEMS: { status: CustomerStatus; label: string; icon: IconType }[] = [
  { status: 'pending', label: 'Pending', icon: Hourglass },
  { status: 'approved', label: 'Approved', icon: CheckCircle2 },
  { status: 'confirmed', label: 'Confirmed', icon: BadgeCheck },
]

/* ------------------------------------------------------------------ *
 * Viewport
 *
 * The rail has three shapes and only one of them is a preference. Below `md`
 * there is no rail at all — navigation is the slide-over sheet at the bottom of
 * this file. Between `md` and `lg` there is not enough width for a 244px column
 * next to a table, so the rail is icons-only regardless of what the operator
 * last chose; the stored preference is honoured again from `lg` up.
 *
 * On a COARSE pointer the forced-icons band stretches to `xl`: a tablet's
 * viewport is all the width it will ever have, and the customer table's tiers
 * re-anchor there too (see LG_UP/XL_UP in customer-table.tsx) on the promise
 * that the rail costs 60px, not 244. A desktop window at 1024-1279 keeps the
 * stored preference exactly as before.
 * ------------------------------------------------------------------ */

const MD_UP = '(min-width: 768px)'
const TABLET_ONLY =
  '(min-width: 768px) and (max-width: 1023.98px), ' +
  '(pointer: coarse) and (min-width: 768px) and (max-width: 1279.98px)'

function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {}
      }
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onChange)
      return () => {
        mql.removeEventListener('change', onChange)
      }
    },
    [query]
  )

  const getSnapshot = React.useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(query).matches
  }, [query])

  return React.useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/* ------------------------------------------------------------------ *
 * Link styling
 *
 * The base class carries layout only — no colour, no background. Active and
 * inactive colours arrive through `activeProps`/`inactiveProps`, which
 * TanStack concatenates onto the class list. Keeping the two sets disjoint is
 * what makes that concatenation deterministic: there is never a pair of
 * conflicting utilities whose winner would depend on stylesheet order.
 * ------------------------------------------------------------------ */

const ITEM_BASE =
  'group relative flex items-center rounded-md text-[14.5px] font-medium ' +
  'transition-colors duration-100 outline-offset-[-2px]'

const ITEM_INACTIVE = 'text-muted hover:bg-s3 hover:text-fg'

const ITEM_ACTIVE =
  'bg-accent-soft text-accent ' +
  "before:absolute before:top-1/2 before:left-0 before:h-5 before:w-[2px] " +
  "before:-translate-y-1/2 before:rounded-full before:bg-accent before:content-['']"

const COUNT_FORMAT = new Intl.NumberFormat('en-US')

function formatCount(n: number | undefined): string {
  return n === undefined ? '—' : COUNT_FORMAT.format(n)
}

/* ------------------------------------------------------------------ *
 * Item
 * ------------------------------------------------------------------ */

interface Shape {
  /** Icons-only, 60px wide. */
  collapsed: boolean
  /** Taller rows for the touch sheet. */
  touch: boolean
}

function itemClass(shape: Shape): string {
  return cn(
    ITEM_BASE,
    shape.touch ? 'h-11' : 'h-9',
    shape.collapsed ? 'justify-center px-0' : 'gap-3 px-3'
  )
}

function ItemBody({
  icon: Icon,
  label,
  count,
  active,
  shape,
}: {
  icon: IconType
  label: string
  count?: number | undefined
  active: boolean
  shape: Shape
}) {
  return (
    <>
      <Icon aria-hidden="true" className="size-4 flex-none" />

      {shape.collapsed ? (
        <span className="sr-only">{label}</span>
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {count === undefined ? null : (
            <span
              className={cn(
                'flex-none font-mono text-[12px] tabular',
                active ? 'text-accent' : 'text-faint'
              )}
            >
              {COUNT_FORMAT.format(count)}
            </span>
          )}
        </>
      )}
    </>
  )
}

function SectionHeading({
  children,
  collapsed,
  first = false,
}: {
  children: string
  collapsed: boolean
  first?: boolean
}) {
  if (collapsed) {
    // A heading has nowhere to go in a 60px column; the rule keeps the grouping.
    return <div className={cn('mx-3 h-px bg-line', first ? 'mt-3 mb-3' : 'my-4')} role="presentation" />
  }
  return (
    <h2
      className={cn(
        'px-3 pb-1.5 text-[11px] font-semibold tracking-[0.08em] text-faint uppercase',
        first ? 'pt-3' : 'pt-6'
      )}
    >
      {children}
    </h2>
  )
}

/* ------------------------------------------------------------------ *
 * Navigation
 * ------------------------------------------------------------------ */

function RailNav({
  shape,
  onNavigate,
  customers,
  telegramCount,
  sessionCount,
  wonCount,
  activeHitCount,
  staleCount,
}: {
  shape: Shape
  onNavigate?: (() => void) | undefined
  customers: Record<CustomerStatus, number> | undefined
  telegramCount: number | undefined
  sessionCount: number | undefined
  wonCount: number | undefined
  activeHitCount: number | undefined
  staleCount: number
}) {
  const { collapsed } = shape

  const staleNote =
    staleCount > 0
      ? `${staleCount} session${staleCount === 1 ? '' : 's'} stopped responding`
      : ''

  return (
    <nav
      aria-label="Main navigation"
      className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pb-4"
    >
      <SectionHeading collapsed={collapsed} first>
        Customers
      </SectionHeading>

      <ul className="space-y-0.5 px-2">
        {/*
          Above the three lists, not below them.

          It is the only entry here that CREATES something rather than filtering
          what exists, and it is the start of the flow the three lists are the
          rest of: create → pending → approved → confirmed. Putting it under
          Confirmed would read as a fourth list.
        */}
        <li>
          <Tooltip content={collapsed ? 'Create customer' : ''} side="right">
            <Link
              to="/customers/new"
              activeOptions={{ exact: true, includeSearch: false }}
              onClick={onNavigate}
              className={itemClass(shape)}
              activeProps={{ className: ITEM_ACTIVE }}
              inactiveProps={{ className: ITEM_INACTIVE }}
            >
              {({ isActive }) => (
                <ItemBody
                  icon={UserPlus}
                  label="Create customer"
                  active={isActive}
                  shape={shape}
                />
              )}
            </Link>
          </Tooltip>
        </li>

        {/*
          Below Create customer and above the three status lists, because it IS
          one of the lists: the pending queue for customers who came in through
          the Telegram agent, lifted out of Pending so the two are never worked
          twice over the same person.

          Its own pathname rather than `/customers/pending?agent=tg` — every
          link here matches with `includeSearch: false`, so two links to one
          pathname would both render as selected. See the route's own note.
        */}
        <li>
          <Tooltip
            content={collapsed ? `Telegram · ${formatCount(telegramCount)}` : ''}
            side="right"
          >
            <Link
              to="/customers/telegram"
              activeOptions={{ exact: true, includeSearch: false }}
              onClick={onNavigate}
              className={itemClass(shape)}
              activeProps={{ className: ITEM_ACTIVE }}
              inactiveProps={{ className: ITEM_INACTIVE }}
            >
              {({ isActive }) => (
                <ItemBody
                  icon={Send}
                  label="Telegram"
                  count={telegramCount}
                  active={isActive}
                  shape={shape}
                />
              )}
            </Link>
          </Tooltip>
        </li>

        {STATUS_ITEMS.map((item) => {
          const count = customers?.[item.status]

          return (
            <li key={item.status}>
              <Tooltip
                content={collapsed ? `${item.label} · ${formatCount(count)}` : ''}
                side="right"
              >
                <Link
                  to="/customers/$status"
                  params={{ status: item.status }}
                  activeOptions={{ exact: true, includeSearch: false }}
                  onClick={onNavigate}
                  className={itemClass(shape)}
                  activeProps={{ className: ITEM_ACTIVE }}
                  inactiveProps={{ className: ITEM_INACTIVE }}
                >
                  {({ isActive }) => (
                    <ItemBody
                      icon={item.icon}
                      label={item.label}
                      count={count}
                      active={isActive}
                      shape={shape}
                    />
                  )}
                </Link>
              </Tooltip>
            </li>
          )
        })}
      </ul>

      <SectionHeading collapsed={collapsed}>Operations</SectionHeading>

      <ul className="space-y-0.5 px-2">
        <li>
          <Tooltip content={collapsed ? 'Schedules' : ''} side="right">
            <Link
              to="/schedules"
              activeOptions={{ exact: true, includeSearch: false }}
              onClick={onNavigate}
              className={itemClass(shape)}
              activeProps={{ className: ITEM_ACTIVE }}
              inactiveProps={{ className: ITEM_INACTIVE }}
            >
              {({ isActive }) => (
                <ItemBody
                  icon={CalendarClock}
                  label="Schedules"
                  active={isActive}
                  shape={shape}
                />
              )}
            </Link>
          </Tooltip>
        </li>

        <li>
          <Tooltip
            content={
              collapsed
                ? `Sessions · ${formatCount(sessionCount)} running`
                : staleNote /* silent unless something is actually wrong */
            }
            side="right"
          >
            <Link
              to="/customers/$status"
              params={{ status: 'approved' }}
              search={{ live: SESSION_LIVE_FILTER }}
              activeOptions={{ exact: true, includeSearch: true }}
              onClick={onNavigate}
              className={itemClass(shape)}
              activeProps={{ className: ITEM_ACTIVE }}
              inactiveProps={{ className: ITEM_INACTIVE }}
            >
              {({ isActive }) => (
                <>
                  <ItemBody
                    icon={Activity}
                    label="Sessions"
                    count={sessionCount}
                    active={isActive}
                    shape={shape}
                  />
                  {/* Kept out of the visual row on purpose — one status element
                      per line — but a screen reader should still hear it. */}
                  {staleNote === '' ? null : <span className="sr-only">{staleNote}</span>}
                </>
              )}
            </Link>
          </Tooltip>
        </li>

        {/*
          Won — a DURABLE outcome, which is why it sits beside Sessions and not
          inside them.

          Everything behind this link already existed: `won_at` is stamped on the
          customer when a shift is actually claimed, `?won=1` filters on it
          against the `won_at_-1` index, `won_at` is a sort key, and the count
          arrives on the same stats payload every other number in this rail comes
          from. The one thing missing was somewhere to click, so a claimed shift
          was recorded perfectly and shown nowhere.

          `live` is deliberately NOT in the search. A customer who won is done
          racing — their heartbeat is stood down the moment the shift is claimed
          — so filtering by a live state would hide the very rows this link
          exists to show. `sort` pins the newest win to the top, which is the
          order an operator wants when three have just landed.

          ── Why `confirmed`, and not `approved` ──
          This pointed at `approved` and showed almost nothing, under a badge
          reading 212. The claim path writes `status: 'confirmed'` in the SAME
          `$set` as `won_at` (see `hiring/won.ts`), so "won" and "still approved"
          are very nearly mutually exclusive — the list was asking for the
          intersection of the two, and the route's `$status` segment means there
          is no way to ask for no status at all.

          The residual gap is honest and small: `unconfirmCustomer` leaves
          `won_at` alone on purpose, so a customer who won and was then moved
          back for another look stays in the counter and drops out of this list.
          That is the one case the write path calls out as unusual, and it is the
          right way round — a stale row in a list is worse than a counter that
          runs one ahead of it.
        */}
        <li>
          <Tooltip
            content={collapsed ? `Won · ${formatCount(wonCount)} shifts claimed` : ''}
            side="right"
          >
            <Link
              to="/customers/$status"
              params={{ status: 'confirmed' }}
              search={{ won: true, sort: 'won_at', dir: 'desc' }}
              activeOptions={{ exact: true, includeSearch: true }}
              onClick={onNavigate}
              className={itemClass(shape)}
              activeProps={{ className: ITEM_ACTIVE }}
              inactiveProps={{ className: ITEM_INACTIVE }}
            >
              {({ isActive }) => (
                <ItemBody
                  icon={Trophy}
                  label="Won"
                  count={wonCount}
                  active={isActive}
                  shape={shape}
                />
              )}
            </Link>
          </Tooltip>
        </li>

        {/*
          Active hits — saw a shift open and did not get it.

          The other outcome of the same race, and the one that had no record at
          all: most sightings are lost to somebody faster, so a customer who
          missed six openings looked exactly like one who had never seen
          anything. A row leaves this list the moment it wins — the claim clears
          the marker in the same write that stamps `won_at` — so the two counts
          never describe the same customer twice.
        */}
        <li>
          <Tooltip
            content={collapsed ? `Active hits · ${formatCount(activeHitCount)} missed` : ''}
            side="right"
          >
            <Link
              to="/customers/$status"
              params={{ status: 'approved' }}
              search={{ activeHit: true, sort: 'active_hit_at', dir: 'desc' }}
              activeOptions={{ exact: true, includeSearch: true }}
              onClick={onNavigate}
              className={itemClass(shape)}
              activeProps={{ className: ITEM_ACTIVE }}
              inactiveProps={{ className: ITEM_INACTIVE }}
            >
              {({ isActive }) => (
                <ItemBody
                  icon={Crosshair}
                  label="Active hits"
                  count={activeHitCount}
                  active={isActive}
                  shape={shape}
                />
              )}
            </Link>
          </Tooltip>
        </li>
      </ul>

      <SectionHeading collapsed={collapsed}>Tools</SectionHeading>

      <ul className="space-y-0.5 px-2">
        <li>
          <Tooltip content={collapsed ? 'Mint Browser' : ''} side="right">
            {/* An external destination, so a plain anchor: it can never be the
                selected route, and it opens in a new tab to keep the console
                (and its SSE stream) alive. */}
            <a
              href={MINT_BROWSER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
              className={cn(itemClass(shape), ITEM_INACTIVE)}
            >
              <ItemBody icon={Globe} label="Mint Browser" active={false} shape={shape} />
            </a>
          </Tooltip>
        </li>

        {/*
          Last in the rail, under the tools, rather than in Operations.

          Everything above this navigates to a LIST of things that are happening
          — customers waiting, schedules, live sessions. This one is the fleet's
          configuration, which is a different kind of destination: visited to
          change how the rest behaves, not to check on it. Settings living at
          the foot of the navigation is where people already look for it.
        */}
        <li>
          <Tooltip content={collapsed ? 'Hunt settings' : ''} side="right">
            <Link
              to="/settings"
              activeOptions={{ exact: true, includeSearch: false }}
              onClick={onNavigate}
              className={itemClass(shape)}
              activeProps={{ className: ITEM_ACTIVE }}
              inactiveProps={{ className: ITEM_INACTIVE }}
            >
              {({ isActive }) => (
                <ItemBody icon={Radar} label="Hunt settings" active={isActive} shape={shape} />
              )}
            </Link>
          </Tooltip>
        </li>
      </ul>
    </nav>
  )
}

/* ------------------------------------------------------------------ *
 * Footer
 * ------------------------------------------------------------------ */

function RailFooter({
  shape,
  meta,
}: {
  shape: Shape
  meta: ConnectionMeta
}) {
  const theme = useUiStore((s) => s.theme)
  const toggleTheme = useUiStore((s) => s.toggleTheme)
  const density = useUiStore((s) => s.density)
  const logout = useLogout()
  /*
   * Read straight from the sfx module rather than mirrored into the UI store.
   *
   * The module is the single writer — it owns the localStorage key and the
   * audio graph — and a copy in zustand would be a second source of truth for
   * one boolean. `useSyncExternalStore` re-renders this button and nothing
   * else; the announce path itself never notifies React at all.
   */
  const soundOn = React.useSyncExternalStore(sfxSubscribe, sfxEnabled, () => false)
  /*
   * On, but the browser has not yet seen the gesture it requires before any
   * page may make a sound. Shown differently from both on and off, because
   * "switched on and silent" is the one state an operator would otherwise read
   * as a broken feature. Resolves itself on their first click anywhere.
   */
  const soundReady = React.useSyncExternalStore(sfxSubscribe, sfxReady, () => false)
  const soundWaiting = soundOn && !soundReady
  const setDensity = useUiStore((s) => s.setDensity)
  const wideColumns = useUiStore((s) => s.wideColumns)
  const toggleWideColumns = useUiStore((s) => s.toggleWideColumns)

  const themeLabel = theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'
  const densityLabel =
    density === 'compact' ? 'Switch to comfortable rows' : 'Switch to compact rows'
  /*
   * Named for what it DOES to the table, not for the state it is in — the same
   * shape as the two labels above, which is what lets one control read
   * correctly whether or not the tooltip is visible.
   */
  const columnsLabel = wideColumns
    ? 'Fit columns to the window'
    : 'Show every column (scrolls sideways)'
  const soundLabel = soundWaiting
    ? 'Sound is on — click anywhere to let the browser start audio'
    : soundOn
      ? 'Mute run sounds'
      : 'Play a sound on claims, misses and wins'

  const side = shape.collapsed ? 'right' : 'top'

  return (
    <div
      className={cn(
        'flex flex-none border-t border-line p-2',
        shape.collapsed ? 'flex-col items-center gap-1' : 'items-center gap-1'
      )}
    >
      <Tooltip content={themeLabel} side={side}>
        <Button
          variant="ghost"
          size={shape.touch ? 'md' : 'sm'}
          iconOnly
          aria-label={themeLabel}
          onClick={toggleTheme}
        >
          {theme === 'dark' ? <Moon aria-hidden="true" /> : <Sun aria-hidden="true" />}
        </Button>
      </Tooltip>

      <Tooltip content={densityLabel} side={side}>
        <Button
          variant="ghost"
          size={shape.touch ? 'md' : 'sm'}
          iconOnly
          aria-label={densityLabel}
          aria-pressed={density === 'compact'}
          onClick={() => {
            setDensity(density === 'compact' ? 'comfortable' : 'compact')
          }}
        >
          {density === 'compact' ? <Rows3 aria-hidden="true" /> : <Rows2 aria-hidden="true" />}
        </Button>
      </Tooltip>

      {/*
        Beside density because it is the same kind of thing: a view preference
        the operator sets once and the customers table reads. It earns its place
        in the rail rather than the table toolbar because the case it exists for
        is a phone in desktop mode, where the rail's icon strip is reachable at
        every width and the toolbar's controls wrap.
      */}
      <Tooltip content={columnsLabel} side={side}>
        <Button
          variant="ghost"
          size={shape.touch ? 'md' : 'sm'}
          iconOnly
          aria-label={columnsLabel}
          aria-pressed={wideColumns}
          onClick={toggleWideColumns}
        >
          {wideColumns ? <Columns3 aria-hidden="true" /> : <Columns2 aria-hidden="true" />}
        </Button>
      </Tooltip>

      {/*
        Sound for the three moments a run reaches that are worth hearing —
        a claim going out, a seat refusing us, a shift taken.

        On by default. Pressing it to switch it back on also supplies the
        gesture the browser requires before any page may make a sound, which is
        why that press answers with a short chirp: one click both asks for
        audio and proves the whole chain works.
      */}
      <Tooltip content={soundLabel} side={side}>
        <Button
          variant="ghost"
          size={shape.touch ? 'md' : 'sm'}
          iconOnly
          aria-label={soundLabel}
          aria-pressed={soundOn}
          onClick={() => {
            setSfxEnabled(!soundOn)
          }}
        >
          {soundOn ? (
            // Dimmed while the audio device is still closed — see `soundWaiting`.
            <Volume2 aria-hidden="true" className={cn(soundWaiting && 'opacity-40')} />
          ) : (
            <VolumeX aria-hidden="true" />
          )}
        </Button>
      </Tooltip>

      <Tooltip content="Sign out" side={side}>
        <Button
          variant="ghost"
          size={shape.touch ? 'md' : 'sm'}
          iconOnly
          aria-label="Sign out"
          disabled={logout.isPending}
          onClick={() => {
            logout.mutate()
          }}
        >
          <LogOut aria-hidden="true" />
        </Button>
      </Tooltip>

      {/* Green up, amber recovering, red down. Never the accent — indigo in this
          corner would read as "selected", not as "connected". */}
      <Tooltip content={meta.tooltip} side={side}>
        {shape.collapsed ? (
          <span className="flex size-8 items-center justify-center">
            <ConnectionDot meta={meta} />
          </span>
        ) : (
          <span className="ml-auto flex h-8 items-center gap-2 rounded-md px-2">
            <ConnectionDot meta={meta} />
            <span className={cn('text-[12.5px] font-medium', meta.colorClass)}>{meta.label}</span>
          </span>
        )}
      </Tooltip>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Brand
 *
 * The brand slot says WHICH console this is. Two of these exist — this one
 * and the UK port — and they are pixel-identical below this row, so the mark
 * is the maple leaf flag rather than a generic glyph, and the wordmark
 * carries "Canada" and the time in Toronto underneath it. region-plate.tsx
 * has the reasoning; only the wordmark's classes live here, because the
 * mobile sheet needs the same wordmark on a `DialogPrimitive.Title`.
 * ------------------------------------------------------------------ */

const WORDMARK =
  'truncate font-display text-[15px] leading-[1.25] font-semibold tracking-[-0.012em] text-fg'

/* ------------------------------------------------------------------ *
 * Rail
 * ------------------------------------------------------------------ */

export function Rail({ realtime, className }: { realtime: RealtimeStatus; className?: string }) {
  const railCollapsed = useUiStore((s) => s.railCollapsed)
  const toggleRail = useUiStore((s) => s.toggleRail)

  const isTablet = useMediaQuery(TABLET_ONLY)
  const collapsed = railCollapsed || isTablet

  const stats = useStats()
  const meta = useConnectionMeta(realtime)

  /*
   * `customers.pending` here is ALREADY net of the segmented queues below it,
   * so the render does no arithmetic and the rail can never disagree with the
   * topbar, which reads the same selector.
   */
  const { customers, segments } = useNavCounts()
  const telegramCount = segments?.telegram
  const live = stats.data?.live
  const sessionCount = live === undefined ? undefined : live.live + live.starting + live.stale
  // Not derived from `live`: a won customer's run has already ended, so the
  // durable count is the only place this number can come from.
  const wonCount = stats.data?.won
  // Same durable source, same reason. Never overlaps `wonCount` — the claim
  // clears the marker in the write that records the win.
  const activeHitCount = stats.data?.activeHits
  const staleCount = stats.data?.staleCount ?? 0

  const shape: Shape = { collapsed, touch: false }

  return (
    <>
      <aside
        className={cn(
          'hidden h-full min-h-0 flex-col border-r border-line bg-s1 md:flex',
          collapsed ? 'w-rail-collapsed' : 'w-rail',
          className
        )}
      >
        {/* One row, three shapes. Between md and lg the collapse is forced by
            the viewport, so no toggle is offered there — a control the next
            resize would silently override is worse than no control. */}
        <div
          className={cn(
            'flex h-topbar flex-none items-center border-b border-line',
            collapsed ? 'justify-center px-1' : 'gap-2 pr-2 pl-3'
          )}
        >
          {collapsed ? (
            <>
              {/* A 60px column has one slot up here, and "which console is
                  this" is not the thing that gives way. So the flag stays: bare
                  on a tablet, and on a desktop as the face of the expand button,
                  which reveals its panel icon on hover — the trick Notion and
                  Linear play with the workspace icon. */}
              {isTablet ? (
                <RegionMark />
              ) : (
                <Tooltip content="Expand navigation" side="right">
                  <Button
                    variant="ghost"
                    size="md"
                    iconOnly
                    aria-label="Expand navigation"
                    aria-expanded={false}
                    onClick={toggleRail}
                    className="group"
                  >
                    <RegionMark className="transition-opacity duration-150 group-hover:opacity-0 group-focus-visible:opacity-0" />
                    <PanelLeftOpen
                      aria-hidden="true"
                      className="absolute inset-0 m-auto opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
                    />
                  </Button>
                </Tooltip>
              )}
              <span className="sr-only">Shift Console, Canada</span>
            </>
          ) : (
            <>
              <RegionPlate title={<span className={WORDMARK}>Shift Console</span>} />

              <Tooltip content="Collapse navigation" side="right">
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label="Collapse navigation"
                  aria-expanded={true}
                  onClick={toggleRail}
                >
                  <PanelLeftClose aria-hidden="true" />
                </Button>
              </Tooltip>
            </>
          )}
        </div>

        <RailNav
          shape={shape}
          customers={customers}
          telegramCount={telegramCount}
          sessionCount={sessionCount}
          wonCount={wonCount}
          activeHitCount={activeHitCount}
          staleCount={staleCount}
        />

        {/* Between the nav and the footer, and outside BOTH. Inside the nav it
            would scroll away on a short viewport — which is the one place a
            health readout must never go — and inside the footer it would sit
            beside the connection dot, where two unrelated status colours a few
            pixels apart read as one confused signal. */}
        <RailSystemChip collapsed={shape.collapsed} touch={shape.touch} />

        <RailFooter shape={shape} meta={meta} />
      </aside>

      <MobileNav
        meta={meta}
        customers={customers}
        telegramCount={telegramCount}
        sessionCount={sessionCount}
        wonCount={wonCount}
        activeHitCount={activeHitCount}
        staleCount={staleCount}
      />
    </>
  )
}

/* ------------------------------------------------------------------ *
 * Mobile slide-over
 *
 * The same navigation, in a focus-trapped Radix dialog anchored to the left
 * edge. Radix gives us Escape, the overlay click, the focus trap and the scroll
 * lock; the two effects below add the rules it cannot know about — close when
 * the route changes, and close if the viewport grows into rail territory while
 * the sheet is open.
 * ------------------------------------------------------------------ */

function MobileNav({
  meta,
  customers,
  telegramCount,
  sessionCount,
  wonCount,
  activeHitCount,
  staleCount,
}: {
  meta: ConnectionMeta
  customers: Record<CustomerStatus, number> | undefined
  telegramCount: number | undefined
  sessionCount: number | undefined
  wonCount: number | undefined
  activeHitCount: number | undefined
  staleCount: number
}) {
  const open = useUiStore((s) => s.mobileNavOpen)
  const setOpen = useUiStore((s) => s.setMobileNavOpen)

  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isDesktop = useMediaQuery(MD_UP)

  React.useEffect(() => {
    setOpen(false)
  }, [pathname, setOpen])

  React.useEffect(() => {
    if (isDesktop) setOpen(false)
  }, [isDesktop, setOpen])

  const close = React.useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const shape: Shape = { collapsed: false, touch: true }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[75] bg-black/50 md:hidden',
            'transition-opacity duration-150 starting:opacity-0'
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed inset-y-0 left-0 z-[76] flex w-[280px] max-w-[86vw] flex-col md:hidden',
            'border-r border-line bg-s1 shadow-e3',
            'pl-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)]',
            'transition-transform duration-150 ease-out starting:-translate-x-full'
          )}
        >
          <div className="flex h-topbar flex-none items-center gap-2 pr-2 pl-3">
            <RegionPlate
              title={<DialogPrimitive.Title className={WORDMARK}>Shift Console</DialogPrimitive.Title>}
            />
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="md" iconOnly aria-label="Close navigation">
                <X aria-hidden="true" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="mx-3 h-px flex-none bg-line" role="presentation" />

          <RailNav
            shape={shape}
            onNavigate={close}
            customers={customers}
            telegramCount={telegramCount}
            sessionCount={sessionCount}
            wonCount={wonCount}
            activeHitCount={activeHitCount}
            staleCount={staleCount}
          />

          <RailSystemChip collapsed={false} touch onNavigate={close} />

          <RailFooter shape={shape} meta={meta} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
